import {
  APP_DATA,
  APP_SECTION,
  APP_SUBMISSION,
  createDefaultFinances,
  createObjectForKeys,
  FINANCES,
  GOALS,
  ID_VERIFY,
  ID_DATA_ENTRY,
  ID_FINISH,
  PRINCIPAL,
  STORAGE_KEYS,
  NEEDS,
  EVENTS,
  getAgeFromDOB,
  ID_CONFIG,
  PARTY,
  OWNER_TYPE,
  ANNUITANT_TYPE,
  BENEFICIARY,
  createPrimaryBeneficiary,
  BENEFICIARY_SURVIVOR_UID, BENEFICIARY_SURVIVOR_FIRST_NAME, BENEFICIARY_SURVIVOR_LAST_NAME, FUNDS, GENERAL
} from './constants'
import {SessionHelper, SESSION_MODES} from './session-helper';
import { v4 as uuidv4 } from 'uuid';
import _ from 'underscore';
import {getExportValueForIdType, getStateExportForRegion} from "./microblink";
import parseAddress from 'parse-address-string';
import moment from "moment";
import {MM_PRODUCT} from "./myga-model";
import {duplicateProduct} from "./product-manager";
import {NassauError, NassauLog} from "./myga-utils";
import EventEmitter from 'eventemitter3';

export let isValid = (obj) => {
  return (!_.isNull(obj) && !_.isUndefined(obj));
};

export let isEqual = (a, b) => {
  return _.isEqual(a,b);
}

export const deepCopy = (a) => {
  try {
    return JSON.parse(JSON.stringify(a));
  } catch (e) {
    NassauLog(`deepCopy error: ${e}`);
  }
  return a;
}

class MygaSession {
  
  constructor() {
    NassauLog('creating session');
    this._currentApp = null;
    this.sessionHelper = new SessionHelper(SESSION_MODES.LOCAL);
    this.eventEmitter = new EventEmitter();
  }
  getCurrentApp() {
    if (!isValid(this._currentApp)) {
      this._currentApp = this.getMygaSessionValue(STORAGE_KEYS.CurrentApplication);
    }
    return this._currentApp;
  }
  setCurrentApp(current) {
    this._currentApp = current;
    if (isValid(current)) {
      this.setMygaSessionValue(STORAGE_KEYS.CurrentApplication, current);
    } else { NassauLog('clearing current application.')
      this.sessionHelper.clearSessionValue(STORAGE_KEYS.CurrentApplication);
    }
    return current;
  }
  
  getCurrentAppData() {
    return isValid(this.getCurrentApp()) ? this._currentApp[APP_DATA.appData] : null;
  }
  
  setCurrentAppSectionData(section, value) {
    if (isValid(this.getCurrentApp())) {
      this._currentApp[APP_DATA.appData][section] = value;
      // NassauLog(`${section} : ${JSON.stringify(this.getCurrentApp()[APP_DATA.appData][section])}`);
      // persist the application data to the session storage
      this.setMygaSessionValue(STORAGE_KEYS.CurrentApplication, this._currentApp);
    }
  }
  
  getCurrentAppSectionData(section, defaultValue = null) {
    // if (isValid(this.getCurrentApp())) {
    //   NassauLog(`${section} : ${JSON.stringify(this._currentApp[APP_DATA.appData][section])}`);
    // }
    return isValid(this.getCurrentApp()) ? this._currentApp[APP_DATA.appData][section] || defaultValue : defaultValue;
  }
  
  isCurrentAppSectionDataAvailable(section) {
    return isValid(this.getCurrentApp()) && isValid(this._currentApp[APP_DATA.appData][section]);
  }
  
  getCurrentAppSubmitStatus() {
    return isValid(this.getCurrentApp()) ? this._currentApp[APP_DATA.appSubmitStatus] : undefined;
  }
  
  setCurrentAppSubmitStatus(value) {
    if (isValid(this.getCurrentApp())) {
      this._currentApp[APP_DATA.appSubmitStatus] = value;
      this.setMygaSessionValue(STORAGE_KEYS.CurrentApplication, this._currentApp);
    }
  }
  
  /**
   * Clears the MYGA session
   */
  clearMygaSession = () => {
    Object.values(STORAGE_KEYS).forEach((key)=>{this.sessionHelper.clearSessionValue(key)});
    this._currentApp = null;
    this.eventEmitter.emit(EVENTS.sessionCleared);
  };
  
  /**
   * Returns the value for a given MYGA session key
   * @param key
   * @param defaultValue
   * @returns {*}
   */
  getMygaSessionValue = (key, defaultValue = null) => {
    // 2021.07.14.ADivelbiss - added code to check the type of the default value to ensure
    // storage as a string.  Needed to set default value as an array
    if (_.isNumber(defaultValue)) {
      defaultValue = defaultValue.toString();
    } else if (_.isObject(defaultValue)) {
      defaultValue = JSON.stringify(defaultValue);
    }
    let value = this.sessionHelper.getSessionValue(key,defaultValue);
    if (value !== null) {
      try {
        value = JSON.parse(value);
      } catch (e) {
      }
    }
    return value
  };
  
  /**
   * Sets the value for a MYGA Session key
   * @param key
   * @param value
   */
  setMygaSessionValue = (key, value) => {
    if (!isValid(value)) {
      this.sessionHelper.clearSessionValue(key);
    } else if ( _.isString(value)) {
      this.sessionHelper.setSessionValue(key, value);
    } else if (_.isNumber(value)) {
      this.sessionHelper.setSessionValue(key, value.toString());
    } else if (_.isObject(value)) {
      this.sessionHelper.setSessionValue(key, JSON.stringify(value));
    } else {
      this.sessionHelper.setSessionValue(key, value);
    }
    return value;
  };
  
  /**
   * Clears and removes the value for a MYGA Session key
   * @param key
   */
  clearMygaSessionValue = (key) => {
    return this.sessionHelper.clearSessionValue(key);
  };
  
  /**
   * CONVENIENCE METHODS
   */
  
  /**
   * return the current guest ID or a new one if one does not exist
   * @returns {*}
   */
  getGuestId = () => {
    return this.getMygaSessionValue(STORAGE_KEYS.GuestId, uuidv4());
  };
  
  /**
   * Returns the principal for a registered user, if it exists,
   * otherwise returns the principal for a guest user,
   * otherwise null
   * @returns {*}
   */
  getPrincipal = () => {
    let principal = this.getMygaSessionValue(STORAGE_KEYS.NsrePrincipalRegistered);
    if (principal !== null) {
      return principal;
    }
    return this.getMygaSessionValue(STORAGE_KEYS.NsrePrincipalGuest);
  };
  
  getClientId = () => {
    let clientId = 'null';
    let principal = this.getPrincipal();
    if (isValid(principal)) {
      if (isValid(principal[PRINCIPAL.clientProfileIds])) {
        if (principal[PRINCIPAL.clientProfileIds].length>0) {
          clientId = principal[PRINCIPAL.clientProfileIds][0];
        }
      }
    }
    return clientId;
  };
  
  getPrincipalToken = (defaultValue = 'null') => {
    let principalToken = defaultValue;
    let principal = this.getPrincipal();
    if (isValid(principal)) {
      if (isValid(principal[PRINCIPAL.pToken])) {
        principalToken = principal[PRINCIPAL.pToken];
      }
    }
    return principalToken;
  };
  
  getAgentInfo = () => {
    return this.getMygaSessionValue(STORAGE_KEYS.AgentInfo);
  };
  
  setAgentInfo = (agentInfo) => {
    this.setMygaSessionValue(STORAGE_KEYS.AgentInfo, agentInfo);
  };
  
  getAgentReferralCode = (defaultValue = 'null') => {
    let agentInfo = mygaSession.getAgentInfo();
    return isValid(agentInfo) && isValid(agentInfo.referralCode) ? agentInfo.referralCode : defaultValue;
  }
  
  setMygaRates = (mygaRates) => {
    return this.setMygaSessionValue(STORAGE_KEYS.WebDataMygaRates, mygaRates);
  };
  
  getMygaRates = () => {
    return this.getMygaSessionValue(STORAGE_KEYS.WebDataMygaRates);
  };
  
  /**
   * Return the myga rate for the given period
   * @param year - period in years
   * @param rates - optional rate data, defaults to current session data
   * @returns {*}
   */
  getMygaRateForPeriod = (year, rates = this.getMygaRates()) => {
    return rates.find(rate => rate.years === `${year}`);
  }
  
  // 2021.01.28.ADivelbiss - added to support landing footnote date.
  setRateFile = (rateFile) => {
    return this.setMygaSessionValue(STORAGE_KEYS.WebDataRateFile, rateFile);
  };
  
  // 2021.01.28.ADivelbiss - added to support landing footnote date.
  getRateFile = () => {
    return this.getMygaSessionValue(STORAGE_KEYS.WebDataRateFile);
  };
  
  // 2022.05.11.ADivelbiss - added to support myannuity rates
  setProductRates = (productFamily, rates) => {
    let pRates = this.getMygaSessionValue(STORAGE_KEYS.WebDataProductRates,{});
    pRates[productFamily] = rates;
    return this.setMygaSessionValue(STORAGE_KEYS.WebDataProductRates, pRates);
  }
  
  getProductRates = (productFamily = null) => {
    let rates = this.getMygaSessionValue(STORAGE_KEYS.WebDataProductRates,{});
    return !productFamily ? rates: rates[productFamily];
  }
  
  // 2022.05.17.ADivelibss - added to support general products using DTOs
  getCurrentAppProductDTOs = () => {
    return this.getCurrentAppSectionData(APP_SECTION.products,[]);
  }
  
  setCurrentAppProductDTOs = (products) => {
    this.setCurrentAppSectionData(APP_SECTION.products,products);
  }
  
  // 2021.04.06.ADivelbiss - added to support new product shopping cart
  getCurrentAppProductCart = () => {
    let cart = this.getCurrentAppSectionData(APP_SECTION.productCart,[]);
    return cart.map(p=>duplicateProduct(p));
  };
  
  getProductFromCart = (pid) => {
    let cart = this.getCurrentAppProductCart();
    if (cart.length>0) {
      let index = cart.findIndex(p=>(p[MM_PRODUCT.productID]===pid));
      if (index>=0) {
        return cart[index];
      }
    }
    return null;
  };
  // 2021.04.06.ADivelbiss - end product shopping card

  // 2021.06.30.ADivelbiss - added to support owner, jointOwner, annuitant, and jointAnnuitant
  getCurrentAppIdConfig = () => {
    return this.getCurrentAppSectionData(APP_SECTION.idConfig, createObjectForKeys(ID_CONFIG,""));
  }

  // 2021.06.30.ADivelbiss - added to support owner, jointOwner, annuitant, and jointAnnuitant - END
  
  getCurrentAppFinances = () => {
    return this.getCurrentAppSectionData(APP_SECTION.finances,createDefaultFinances());
  };
  
  getCurrentAppNeeds = () => {
    return this.getCurrentAppSectionData(APP_SECTION.needs,createObjectForKeys(NEEDS,""));
  };

  getCurrentAppFunds = () => {
    return this.getCurrentAppSectionData(APP_SECTION.funds,createObjectForKeys(FUNDS,""));
  };

  getCurrentAppGeneral = () => {
    return this.getCurrentAppSectionData(APP_SECTION.general,createObjectForKeys(GENERAL,""));
  };
  
  getCurrentAppGoals = () => {
    return this.getCurrentAppSectionData(APP_SECTION.goals,createObjectForKeys(GOALS,""));
  };

  getCurrentAppIdVerify = () => {
      return this.getCurrentAppSectionData(APP_SECTION.identity1,createObjectForKeys(ID_VERIFY,''));
  };

  getCurrentAppIdDataEntry = () => {
    return this.getCurrentAppSectionData(APP_SECTION.identity2,createObjectForKeys(ID_DATA_ENTRY,""));
  };

  getCurrentAppIdFinish = () => {
    return this.getCurrentAppSectionData(APP_SECTION.identity3,createObjectForKeys(ID_FINISH,""));
  };

  setInfoFromScanResult = async (response) => {

    let idDataEntry = this.getCurrentAppIdDataEntry();

    idDataEntry[ID_DATA_ENTRY.firstName] = response.result.firstName;
    idDataEntry[ID_DATA_ENTRY.lastName] = response.result.lastName;

    let issuingState = getStateExportForRegion(response.result.classInfo.region);
    idDataEntry[ID_DATA_ENTRY.issuingState] = issuingState.abbreviation;

    try {

      let cleanedUpAddressString = response.result.address.replace(/(\r\n|\n|\r)/gm,", ");
      let address = await this.parseAddressString(cleanedUpAddressString);
      idDataEntry[ID_DATA_ENTRY.addressStreet] = address.street_address1;
      idDataEntry[ID_DATA_ENTRY.addressCity] = address.city;

      //Some issue with the state parsing when it looks like it should parse it, so I added am override to use the document
      //region when the state cannot be parsed.
      idDataEntry[ID_DATA_ENTRY.addressState] = address.state?address.state:issuingState.abbreviation;
      idDataEntry[ID_DATA_ENTRY.addressZip] = address.postal_code;

    }catch (e) {
      NassauLog("Could not parse the address string")
    }

    idDataEntry[ID_DATA_ENTRY.idNumber] = response.result.documentNumber;
    idDataEntry[ID_DATA_ENTRY.gender] = response.result.sex==="M"?"Male":"Female";

    try {
      idDataEntry[ID_DATA_ENTRY.dob] = moment(response.result.dateOfBirth.originalString).format("MM-DD-YYYY");
    } catch (e) {
      NassauLog("Could not created date of birth:"+e.description);
    }

    try {
      idDataEntry[ID_DATA_ENTRY.idExpiration] = moment(response.result.dateOfExpiry.originalString).format("MM-DD-YYYY");
    } catch (e) {
      NassauLog("Could not created date of expiry:"+e.description);
    }

    idDataEntry[ID_DATA_ENTRY.idType] = getExportValueForIdType(response.result.classInfo.type);

    this.setCurrentAppSectionData(APP_SECTION.identity2, idDataEntry);

  };

  parseAddressFromStringPromise = (addressString) => {

    return new Promise((resolve, reject) => {
      parseAddress(addressString,(err, addressObj) => {
        if (!err && addressObj) {
          resolve(addressObj);
        } else {
          reject(err.description);
        }
      });
    });
  };

  parseAddressString = async (addressString) => {
    try {
      const result = await this.parseAddressFromStringPromise(addressString);
      NassauLog(result);
      return result;
    } catch(error) {
      NassauError("ERROR:" + error);
      return null;
    }
  };
  
  // Age calculation function from original DTC MYGA project
  // the date format of data[fname] is assumed MM-DD-YYYY
  getOwnerAge = () => {
    const owner = this.getCurrentAppOwner();
    return getAgeFromDOB(owner,PARTY.dob);
  }
  
  // 2021.10.14.ADivelbiss - methods added to support owner/annuitants
  getCurrentAppParties = () => {
    return this.getCurrentAppSectionData(APP_SECTION.parties,[]);
  }
  setCurrentAppParties = (nextParties) => {
    this.setCurrentAppSectionData(APP_SECTION.parties,nextParties);
  }
  getCurrentPartyIndex = () => {
    return this.getMygaSessionValue(STORAGE_KEYS.CurrentPartyIndex,0);
  }
  setCurrentPartyIndex = (index) => {
    return this.setMygaSessionValue(STORAGE_KEYS.CurrentPartyIndex, index);
  }
  
  currentAppHasOwner = () => {
    return this.getCurrentAppParties().filter((person)=>{
      return (person[PARTY.ownerType] === OWNER_TYPE.primary);
    }).length>0;
  }
  currentAppHasJointOwner = () => {
    return this.getCurrentAppParties().filter((person)=>{
      return (person[PARTY.ownerType] === OWNER_TYPE.joint);
    }).length>0;
  }
  currentAppHasAnnuitant = () => {
    return this.getCurrentAppParties().filter((person)=>{
      return (person[PARTY.annuitantType] === ANNUITANT_TYPE.primary);
    }).length>0;
  }
  currentAppHasJointAnnuitant = () => {
    return this.getCurrentAppParties().filter((person)=>{
      return (person[PARTY.annuitantType] === ANNUITANT_TYPE.joint);
    }).length>0;
  }
  
  /**
   * Removes the JointOwner if present.
   * Othwerise, does nothing.
   * @returns true if a change was made. otherwise false
   */
  currentAppRemoveJointOwner = () => {
    let parties = [...this.getCurrentAppParties()];
    let index = parties.findIndex(p=>(p[PARTY.ownerType]===OWNER_TYPE.joint));
    if (index>=0) {
      parties.splice(index,1);
      mygaSession.setCurrentAppSectionData(APP_SECTION.parties,parties);
      return true;
    }
    return false;
  }

  /**
   * Removes the JointAnnuitant if present.
   * Othwerise, does nothing.
   * @returns true if a change was made. otherwise false
   */
  currentAppRemoveJointAnnuitant = () => {
    let parties = [...this.getCurrentAppParties()];
    let index = parties.findIndex(p=>(p[PARTY.annuitantType]===ANNUITANT_TYPE.joint));
    if (index>=0) {
      parties.splice(index,1);
      mygaSession.setCurrentAppSectionData(APP_SECTION.parties,parties);
      return true;
    }
    return false;
  }

  getCurrentAppOwner = (parties = this.getCurrentAppParties()) => {
    return parties.find((person)=>(person[PARTY.ownerType]===OWNER_TYPE.primary));
    // const owners = this.getCurrentAppParties().filter((person)=>{
    //   return (person[PARTY.ownerType] === OWNER_TYPE.primary);
    // });
    // return owners.length>0 ? {...owners[0]} : null;
  }

  setCurrentAppOwner = (owner) => {
    let parties = [...this.getCurrentAppParties()];
    let index = parties.findIndex(p=>(p[PARTY.ownerType]===OWNER_TYPE.primary));
    if (index>=0) {
      parties[index] = owner;
      mygaSession.setCurrentAppSectionData(APP_SECTION.parties,parties);
      return true;
    }
    return false;
  }

  getCurrentAppJointOwner = (parties = this.getCurrentAppParties()) => {
    return parties.find((person)=>(person[PARTY.ownerType]===OWNER_TYPE.joint));
    // const jointOwners = this.getCurrentAppParties().filter((person)=>{
    //   return (person[PARTY.ownerType] === OWNER_TYPE.joint);
    // });
    // return jointOwners.length>0 ? {...jointOwners[0]} : null;
  }

  setCurrentAppJointOwner = (jointOwner) => {
    let parties = [...this.getCurrentAppParties()];
    let index = parties.findIndex(p=>(p[PARTY.ownerType]===OWNER_TYPE.joint));
    if (index>=0) {
      parties[index] = jointOwner;
      mygaSession.setCurrentAppSectionData(APP_SECTION.parties,parties);
      return true;
    }
    return false;
  }
  getCurrentAppAnnuitant = (parties = this.getCurrentAppParties()) => {
    return parties.find((person)=>(person[PARTY.annuitantType]===ANNUITANT_TYPE.primary));
    // const annuitants = this.getCurrentAppParties().filter((person)=>{
    //   return (person[PARTY.annuitantType] === ANNUITANT_TYPE.primary);
    // });
    // return annuitants.length>0 ? {...annuitants[0]} : null;
  }

  setCurrentAppAnnuitant = (annuitant) => {
    let parties = [...this.getCurrentAppParties()];
    let index = parties.find(p=>(p[PARTY.annuitantType]===ANNUITANT_TYPE.primary));
    if (index>=0) {
      parties[index] = annuitant;
      mygaSession.setCurrentAppSectionData(APP_SECTION.parties,parties);
      return true;
    }
    return false;
  }

  getCurrentAppJointAnnuitant = (parties = this.getCurrentAppParties()) => {
    return parties.find((person)=>(person[PARTY.annuitantType]===ANNUITANT_TYPE.joint));
    // const jointAnnuitants = this.getCurrentAppParties().filter((person)=>{
    //   return (person[PARTY.annuitantType] === ANNUITANT_TYPE.joint);
    // });
    // return jointAnnuitants.length>0 ? {...jointAnnuitants[0]} : null;
  }

  setCurrentAppJointAnnuitant = (jointAnnuitant) => {
    let parties = [...this.getCurrentAppParties()];
    let index = parties.find(p=>(p[PARTY.annuitantType]===ANNUITANT_TYPE.joint));
    if (index>=0) {
      parties[index] = jointAnnuitant;
      mygaSession.setCurrentAppSectionData(APP_SECTION.parties,parties);
      return true;
    }
    return false;
  }

  addCurrentAppBene = (bene) => {
    NassauLog(`Entering addCurrentAppBene`);
    let benes = [...this.getCurrentAppBenes()];
    // Check to see if primaryBene's uid already exists.
    //  If it exists -> updateBene;
    //  If not -> createPrimaryBeneFromParty;
    let intIndex = benes.findIndex(b=>(b[BENEFICIARY.uid]===bene[BENEFICIARY.uid]));
    let index = benes.find(b=>(b[BENEFICIARY.uid]===bene[BENEFICIARY.uid]));
    // NassauLog(`Benes: ${benes[0]}`);
    // NassauLog(`indexed id (existing) = ${benes[index][BENEFICIARY.uid]}`);
    /*NassauLog("------------------");
    NassauLog(`"new" id = ${bene[BENEFICIARY.uid]}`);
    NassauLog("------------------");*/
    if (index>=0) {
      benes[index] = bene;
    } else if (benes.length < 4){
      benes.push(bene);
    } else {
      return false;
    }
    mygaSession.setCurrentAppSectionData(APP_SECTION.benes,benes);
    return true;
  }
  
  // 2022.01.11.ADivelbiss - remove the given bene if exists
  removeCurrentAppBene = (bene) => {
    // get the current benes array
    let benes = [...this.getCurrentAppBenes()];
    // get the index of the given bene
    let index = benes.findIndex(b=>(b[BENEFICIARY.uid]===bene[BENEFICIARY.uid]));
    if (index>=0) {
      // remove the bene
      benes = benes.splice(index,1);
      // save the updated benes array
      mygaSession.setCurrentAppSectionData(APP_SECTION.benes,benes);
    }
  }

  addCurrentAppSurvivingSpouse= () => {
    let survivor = createPrimaryBeneficiary();
    survivor[BENEFICIARY.uid] = BENEFICIARY_SURVIVOR_UID;
    survivor[BENEFICIARY.firstName] = BENEFICIARY_SURVIVOR_FIRST_NAME;
    survivor[BENEFICIARY.lastName] = BENEFICIARY_SURVIVOR_LAST_NAME;

    mygaSession.addCurrentAppBene(survivor);
  }
  
  // 2022.01.11.ADivelbiss - remove the surviving spouse as bene
  removeCurrentAppSurvivingSpouse = () => {
    let benes = [...this.getCurrentAppBenes()];
    // get the index of the given bene
    let index = benes.findIndex(b=>(b[BENEFICIARY.uid]===BENEFICIARY_SURVIVOR_UID));
    if (index>=0) {
      // remove the bene
      benes = benes.splice(index,1);
      // save the updated benes array
      mygaSession.setCurrentAppSectionData(APP_SECTION.benes,benes);
    }
    return (index>=0);
  }

  getCurrentAppBenes = () => {
    return this.getCurrentAppSectionData(APP_SECTION.benes,[]);
  };

  getCurrentAppPayments = () => {
    return this.getCurrentAppSectionData(APP_SECTION.payments,{});
  };
  
  //
  // getOwner = () => this.getCurrentAppParties().filter(p=>(p[PARTY.ownerType]===OWNER_TYPE.primary))[0];
  //
  // validatePayors = () => {
  //   // Object = APPLICATION.payments;
  //   //const ownerUID = this.getOwner()[PARTY.uid];
  //   //NassauLog(`ownerUID: ${ownerUID}`);
  //   let payments = this.getCurrentAppPayments();
  //   let paymentsData = Object.values(payments);
  //   NassauLog(JSON.stringify(paymentsData, null, 2));
  //   // Process each product
  //   paymentsData.forEach((product) => {
  //     product.forEach((payment) => {
  //       NassauLog(`uid: ${payment.payorUid}`);
  //       // if(this.getCurrentAppParties().filter(p=>(p[PARTY.uid] === payment.payorUid)).length === 0){
  //       //   payment.payorUid = ownerUID;
  //       // }
  //     })
  //   })
  //   this.setCurrentAppSectionData(APP_SECTION.payments, payments);
  //
  //
  // }
  getCurrentAppSubmission = () => {
    return this.getCurrentAppSectionData(APP_SECTION.submission,createObjectForKeys(APP_SUBMISSION,""));
  };

  setBankRates = (rates) => {
    return this.setMygaSessionValue(STORAGE_KEYS.WebDataBankRates, rates);
  };

  getBankRates = () => {
    return this.getMygaSessionValue(STORAGE_KEYS.WebDataBankRates);
  };

  setTreasuryRates = (rates) => {
    return this.setMygaSessionValue(STORAGE_KEYS.WebDataTreasuryRates, rates);
  };

  getTreasuryRates = () => {
    return this.getMygaSessionValue(STORAGE_KEYS.WebDataTreasuryRates);
  };

  getHasOtherContracts = () => {
    return this.getCurrentAppFinances()[FINANCES.otherContracts] === 'Yes';
  };
  
  getShowAccountPage = () => {
    return this.getPrincipal() !== null && this.getAgentInfo() !== null;
  };
  
  getShowGuestEmailPage = () => {
    return this.getPrincipal() === null;
  };
  
  getShowAgentPage = () => {
    return this.getAgentInfo() === null;
  };
  
  getReturnToPage = () => {
    return this.getMygaSessionValue(STORAGE_KEYS.ReturnToPage);
  };
  setReturnToPage = (prevPage) => {
    return this.setMygaSessionValue(STORAGE_KEYS.ReturnToPage, prevPage);
  };
  clearReturnToPage = () => {
    return this.clearMygaSessionValue(STORAGE_KEYS.ReturnToPage);
  };
  setShowCancel = flag => {
    return this.setMygaSessionValue(STORAGE_KEYS.ShowCancel, flag);
  }
  getShowCancel = () => this.getMygaSessionValue(STORAGE_KEYS.ShowCancel);

  // 2022.07.05.ADivelbiss - added for test recording
  setErrorMessage = message => this.setMygaSessionValue(STORAGE_KEYS.ErrorMessage, message);
  getErrorMessage = () => this.getMygaSessionValue(STORAGE_KEYS.ErrorMessage,'');
  getPageStack = () => {
    let pageStack = this.getMygaSessionValue(STORAGE_KEYS.PageStack,[]);
    if (pageStack.length===0) {
      pageStack = this.getMygaSessionValue(STORAGE_KEYS.PrevPageStack,[]);
    }
    return pageStack;
  }
  // 2022.07.05.ADivelbiss - end added for test recording
  
  getDate = (string, format) => {
    return moment(string,format).toDate();
  }
  
  getEOD = (string, format) => {
    return moment(string,format).startOf('day').add(1439,'minutes').toDate();
  }
} // MygaSession


export const mygaSession = new MygaSession();


/**
 * Debounce timer with function
 * @param f - function to call when the timer fires
 * @param delay
 * @returns {number}
 */
export let funcDebounce = (f, delay) => {
  return setTimeout(() => {
    f();
  }, delay);
};

